/**
 * Feb-2026 — Supplier Type targeting segments.
 *
 * Single source of truth used by the RFQ Wizard Step 2 (visible
 * supplier list + Select-all-matching + Select-recommended), Step 3
 * (segment label + count-by-type + warnings), and the persisted
 * `type_segment` draft field.
 *
 * The canonical Type labels come from the backend (see
 * `SUPPLIER_TYPE_LABELS` in `/app/backend/routes/sourcing_rfq.py`).
 * DO NOT duplicate that list — reference it here so any future rename
 * only needs a single import.
 */

// Canonical Type labels — MUST mirror backend SUPPLIER_TYPE_LABELS.
export const CANONICAL_TYPES = [
  'Functional B/C',
  'Salvage / Refurbishment',
  'Both',
  'Review Before RFQ',
  'Do Not RFQ - Listed Inventory Only',
];

// Types the wizard treats as "elevated risk" — recipients that either
// need pre-send human review or must never receive automated RFQs.
export const RESTRICTED_TYPES = new Set([
  'Review Before RFQ',
  'Do Not RFQ - Listed Inventory Only',
]);

export const DO_NOT_RFQ_TYPE = 'Do Not RFQ - Listed Inventory Only';

// Targeting segments (populate the Step 2 filter dropdown).
//   value           — the string persisted on the campaign document
//   label           — dropdown label (user-facing)
//   exactTypes      — Type labels that satisfy the segment
//   includeBlank    — when true, suppliers with no Type also match
export const SUPPLIER_TYPE_SEGMENTS = [
  {
    value: '',
    label: 'All Eligible Types',
    exactTypes: ['Functional B/C', 'Salvage / Refurbishment', 'Both'],
    includeBlank: false,
  },
  {
    value: 'functional_bc_campaign',
    label: 'Functional B/C Campaign',
    exactTypes: ['Functional B/C', 'Both'],
    includeBlank: false,
  },
  {
    value: 'salvage_refurb_campaign',
    label: 'Salvage / Refurbishment Campaign',
    exactTypes: ['Salvage / Refurbishment', 'Both'],
    includeBlank: false,
  },
  {
    value: 'functional_bc_only',
    label: 'Functional B/C Only',
    exactTypes: ['Functional B/C'],
    includeBlank: false,
  },
  {
    value: 'salvage_refurb_only',
    label: 'Salvage / Refurbishment Only',
    exactTypes: ['Salvage / Refurbishment'],
    includeBlank: false,
  },
  {
    value: 'both_only',
    label: 'Both Only',
    exactTypes: ['Both'],
    includeBlank: false,
  },
  {
    value: 'review_before_rfq',
    label: 'Review Before RFQ',
    exactTypes: ['Review Before RFQ'],
    includeBlank: false,
  },
  {
    value: 'do_not_rfq',
    label: 'Do Not RFQ - Listed Inventory Only',
    exactTypes: ['Do Not RFQ - Listed Inventory Only'],
    includeBlank: false,
  },
  {
    value: 'unclassified',
    label: 'Unclassified (blank Type)',
    exactTypes: [],
    includeBlank: true,
  },
];

const _segmentByValue = SUPPLIER_TYPE_SEGMENTS.reduce((acc, s) => {
  acc[s.value] = s;
  return acc;
}, {});

export const getSegmentByValue = (value) =>
  _segmentByValue[value || ''] || _segmentByValue[''];

export const getSegmentLabel = (value) => getSegmentByValue(value).label;

/**
 * Return true when a supplier row matches the given segment value.
 * `supplier.supplier_type` is the canonical label (or blank/null/
 * undefined when unclassified).
 */
export const matchesTypeSegment = (supplier, segmentValue) => {
  const seg = getSegmentByValue(segmentValue);
  const raw = supplier?.supplier_type;
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  if (!trimmed) return !!seg.includeBlank;
  return seg.exactTypes.includes(trimmed);
};

/** Count suppliers by canonical Type + `Unclassified` bucket. */
export const countByType = (suppliers) => {
  const counts = { Unclassified: 0 };
  CANONICAL_TYPES.forEach((t) => { counts[t] = 0; });
  (suppliers || []).forEach((s) => {
    const raw = typeof s?.supplier_type === 'string' ? s.supplier_type.trim() : '';
    if (!raw) { counts.Unclassified += 1; return; }
    if (counts[raw] === undefined) counts[raw] = 0;
    counts[raw] += 1;
  });
  return counts;
};

/** Return the list of restricted-Type suppliers a caller must warn about. */
export const flagRestrictedSuppliers = (suppliers) =>
  (suppliers || []).filter((s) => {
    const raw = typeof s?.supplier_type === 'string' ? s.supplier_type.trim() : '';
    return raw && RESTRICTED_TYPES.has(raw);
  });
